import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import styles from '../../assets/styles/YogaCourse/100Hours.module.css';
import Refund from 'components/Refund/refund';
import OurCourses from 'components/OurCourses/OurCourses';
import Hours24 from 'components/Hours24/Hours24';
import heroVideo from '../../assets/images/100hrs.webp';
import BooksSection from 'components/MyBooks/Bookssection';
import HowToReach from 'components/Homepage/HowToReach';
import Form100 from 'components/YogaCourses/Form100';
const ENV_API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";

const API_BASE = `${ENV_API_BASE_URL}/api`;
const ASSET_BASE = `${ENV_API_BASE_URL}`;

const toAssetUrl = (value) => {
  if (!value) return '';
  if (typeof value !== 'string') return value;
  if (value.startsWith('http') || value.startsWith('data:')) return value;
  return `${ASSET_BASE}/${value.replace(/^\/+/, '')}`;
};

const DEFAULT_HERO = {
  quoteText: 'Experience the Best Accommodation and Food at Our Yoga School',
  title: 'Yoga Retreat in Rishikesh, India',
  breadcrumbLabel: 'Yoga Retreat',
  bannerImage: '',
};

const DEFAULT_OVERVIEW = {
  tag: 'Yoga Retreat Overview',
  title: 'A Life-Changing Journey Into Holistic Healing, Self-Care, and Inner Peace',
  description:
    'Step into serenity with our wellness yoga retreat in Rishikesh, India. Nestled in the heart of the Himalayas by the sacred Ganga River, Rishikesh is the global capital of yoga, offering the perfect setting to reconnect with your body, mind, and soul. Whether you\'re seeking to reduce stress, improve flexibility, or simply take a break from the busy world, our retreat combines daily yoga, meditation, pranayama, nature walks, Ayurvedic meals, and mindful living practices. Led by experienced teachers, this immersive program is suitable for all levels—from beginners to seasoned yogis.',
};

const DEFAULT_INFO_CARDS = [
  {
    title: 'Duration',
    tag: '7–10 Days',
    detail: '📅 Flexible retreat duration (min. 3 days)',
    icon: '📅',
  },
  {
    title: 'Level',
    tag: 'All Levels',
    detail: '📊 Beginners to seasoned yogis welcome',
    icon: '📊',
  },
  {
    title: 'Includes',
    tag: 'All-Inclusive',
    detail: '🏆 Meals, accommodation & excursions',
    icon: '🏆',
  },
  {
    title: 'Location',
    tag: 'Rishikesh, India',
    detail: '📍 Jonk Village, near Ram Jhula',
    icon: '📍',
  },
];

const DEFAULT_WHY = {
  tag: 'Why Choose Our School?',
  title: 'Discover the Magic of Our Wellness Yoga Retreat in Rishikesh',
  description:
    'We offer a unique blend of authentic yogic experience, spiritual ambience, personalized care, and holistic wellness in the heart of the yoga capital of the world.',
  items: [
    {
      icon: '🕉️',
      title: 'Authentic Yogic Experience',
      description:
        'Situated near the holy Ganges and surrounded by Himalayan foothills, our retreat is rooted in traditional Hatha and Ashtanga yoga taught by Indian masters with decades of experience.',
    },
    {
      icon: '✨',
      title: 'Spiritual Ambience',
      description:
        'From daily aarti ceremonies to the healing hum of mantras, you\'ll find spiritual energy in every corner—Rishikesh is not just a place; it\'s a vibration that heals.',
    },
    {
      icon: '🧘‍♀️',
      title: 'Personalized & Inclusive',
      description:
        'We welcome all levels—from complete beginners to seasoned practitioners. Our small-group format ensures personal attention, emotional support, and spiritual depth.',
    },
    {
      icon: '🌿',
      title: 'Holistic Wellness',
      description:
        'Beyond asanas, you\'ll experience pranayama, meditation, sound healing, yogic philosophy, and Ayurvedic practices designed to balance your entire being.',
    },
  ],
};

const DEFAULT_CURRICULUM = {
  tag: 'Retreat Highlights',
  title: 'Everything Included in Your Wellness Yoga Retreat Experience',
  items: [
    {
      category: 'Yoga & Movement',
      items: [
        'Daily Asana classes to strengthen and balance',
        'Dynamic Yoga Flow & Yin Yoga',
        'Slow Hatha Yoga & Sunrise Flow',
        'Surya Namaskar practice',
      ],
    },
    {
      category: 'Meditation & Breathwork',
      items: [
        'Guided Meditation & Pranayama',
        'Mandala Meditation',
        'Loving-Kindness Meditation',
        'Yoga Nidra sessions',
      ],
    },
    {
      category: 'Healing Practices',
      items: [
        'Sound Healing Sessions & Kirtan Evenings',
        'Fire Ritual (Havan)',
        'Private Reiki Healing Sessions',
        'Shatkarma (Yogic Cleansing) techniques',
      ],
    },
    {
      category: 'Ayurveda & Wellness',
      items: [
        'Ayurvedic Doctor Consultation & Therapies',
        'Ayurvedic Lifestyle Workshops',
        'Ayurvedic Cooking Classes',
        'Detox Juices & Herbal Teas',
      ],
    },
    {
      category: 'Nourishment',
      items: [
        'Three Sattvic Meals Daily',
        'Gluten-free & dairy-free options',
        'Vegan-friendly meals available',
        'Refreshing herbal teas & fresh juices',
      ],
    },
    {
      category: 'Self-Inquiry & Growth',
      items: [
        'Self-Inquiry Circles & Journaling',
        'Yoga Philosophy Workshop',
        'Himalayan Sunrise Trek',
        'Ganga Aarti Ceremony',
      ],
    },
  ],
};

const DEFAULT_SCHEDULE = {
  tag: 'Retreat Schedule',
  title: 'Your Day-by-Day Retreat Journey',
  items: [
    { time: 'Day 1', activity: 'Arrival & Intention Setting — Opening Ceremony & Gentle Hatha Yoga' },
    { time: 'Day 2', activity: 'Detoxification Begins — Asana, Pranayama & Ayurvedic Therapy' },
    { time: 'Day 3', activity: 'Mind-Body Balance — Yin Yoga, Pranayama & Sound Bath' },
    { time: 'Day 4', activity: 'Emotional Healing & Stillness — Slow Hatha, Excursions & Loving-Kindness Meditation' },
    { time: 'Day 5', activity: 'Rejuvenation & Empowerment — Dynamic Yoga Flow, Excursions & Kirtan' },
    { time: 'Day 6', activity: 'Awakening & Expansion — Asana, Pranayama & Ayurveda Workshop' },
    { time: 'Day 7', activity: 'Inner Clarity & Self-Inquiry — Asana, Excursions & Yoga Nidra' },
    { time: 'Day 8', activity: 'Rest & Recharge — Sunrise Flow, Philosophy Workshop & Ganga Aarti' },
    { time: 'Day 9', activity: 'Awakened Living — Asana, River Rafting & Evening Meditation' },
    { time: 'Day 10', activity: 'Departure & Closing — Hatha Yoga, Pranayama & Closing Ceremony' },
  ],
};

const DEFAULT_FEATURES = {
  tag: 'Retreat Features',
  title: 'Everything You Need for a Transformative Yoga Retreat',
  items: [
    { icon: '🧘', title: 'Expert Teachers', desc: 'Experienced Indian yoga masters guiding your practice' },
    { icon: '🏔️', title: 'Himalayan Setting', desc: 'Nestled in Jonk Village near the sacred Ganga River' },
    { icon: '🌿', title: 'Sattvic Meals', desc: 'Pure vegetarian food with vegan & gluten-free options' },
    { icon: '🏠', title: 'Cozy Accommodation', desc: 'Private or twin-shared rooms with modern amenities' },
    { icon: '🧘‍♀️', title: 'Small Groups', desc: 'Personalized attention in intimate group settings' },
    { icon: '✈️', title: 'Airport Pickup', desc: 'Complimentary transfer from Dehradun Jolly Grant Airport' },
    { icon: '🕉️', title: 'Spiritual Rituals', desc: 'Havan, Ganga Aarti and daily spiritual ceremonies' },
    { icon: '💆', title: 'Ayurvedic Therapies', desc: 'Healing consultations and holistic wellness practices' },
  ],
};

const DEFAULT_INCLUDED_SECTION = {
  tag: "What's Included",
  title: 'Everything You Need for a Comfortable Retreat Stay',
  includedTitle: 'What does the retreat fee include?',
  notIncludedTitle: 'What is not included in the retreat fee?',
  toBringTitle: 'What to bring with you?',
  items: [
    'Accommodation (private or twin-shared rooms)',
    '3 sattvic vegetarian meals daily',
    'Detox juices & herbal teas throughout the day',
    'All yoga, meditation & pranayama sessions',
    'Ayurvedic doctor consultation',
    'Sound healing & Reiki sessions',
    'Weekend excursions & sightseeing',
    'Himalayan sunrise trek or Ganga Aarti',
    'Fire ceremony (Havan)',
    'Complimentary pickup from Dehradun Airport',
    'Yoga Philosophy & Ayurveda workshops',
    '24/7 WiFi access',
  ],
  notIncluded: [],
  toBringImage: '',
};

const DEFAULT_TESTIMONIALS = {
  tag: 'What Our Guests Say',
  title: 'Hear from Those Who Experienced the Transformation',
  items: [
    {
      name: 'Sarah Johnson',
      country: 'USA',
      text: 'This retreat was the most transformative experience of my life. The teachers are incredibly knowledgeable, and the spiritual energy of Rishikesh is truly magical. I left feeling refreshed, rebalanced, and inspired.',
    },
    {
      name: 'Michael Chen',
      country: 'Canada',
      text: 'The perfect wellness escape. Small group settings meant I received personal attention, and the combination of yoga, Ayurveda, and nature excursions was exactly what I needed to reset.',
    },
    {
      name: 'Emma Williams',
      country: 'UK',
      text: 'The spiritual atmosphere of Rishikesh combined with the warm community made this an unforgettable journey. Waking up each day to the Ganga and morning yoga was life-changing.',
    },
  ],
};

const DEFAULT_FAQS = {
  tag: 'Frequently Asked Questions',
  title: 'Get Answers to Common Questions About Our Retreat',
  items: [
    {
      q: 'Do I need to be experienced in yoga to join this retreat?',
      a: 'Not at all! Our retreat is open to all levels, including complete beginners. Our teachers provide personalized guidance based on your level and needs.',
    },
    {
      q: 'Is airport pickup included?',
      a: 'Yes, complimentary pickup from Dehradun Jolly Grant Airport (DED) is included. Drop-off can also be arranged at an additional charge.',
    },
    {
      q: 'What should I bring for the retreat?',
      a: 'Comfortable yoga clothes, a refillable water bottle, personal toiletries, and an open heart. Yoga mats are available on-site but feel free to bring your own.',
    },
    {
      q: 'Is this retreat suitable for solo travelers?',
      a: 'Absolutely! Many of our guests travel alone and leave with lifelong friends. You\'ll feel at home in our safe, inclusive, and supportive environment.',
    },
    {
      q: 'Are there sightseeing or excursions included?',
      a: 'Yes! We offer Himalayan sunrise hikes, visits to local temples, Ganga Aarti at Parmarth Niketan, and nature walks—all included in the program.',
    },
    {
      q: 'Can I join for fewer or more days?',
      a: 'Yes, while our standard retreat runs for 7–10 days, we offer flexible options (minimum 3 days). Let us know your travel dates and we\'ll guide you accordingly.',
    },
  ],
};

const DEFAULT_CTA = {
  title: 'Ready to Begin Your Wellness Journey?',
  subtitle: 'Transform your life through yoga. Join us in Rishikesh for an unforgettable retreat experience.',
  primaryLabel: 'Apply Now',
  primaryLink: '/BookingForm',
  secondaryLabel: 'Contact Us',
  secondaryLink: '/contact-us',
};

export default function YogaRetreatRishikesh() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [course, setCourse] = useState(null);

  const courseSlug = slug || 'yoga-retreat-rishikesh';

  useEffect(() => {
    const fetchCourse = async () => {
      try {
        const res = await axios.get(`${API_BASE}/courses/slug/${courseSlug}`);
        setCourse(res.data?.data || null);
      } catch (error) {
        console.error('Failed to load retreat data', error);
      }
    };

    fetchCourse();
  }, [courseSlug]);

  const content = course?.content || {};

  const hero = { ...DEFAULT_HERO, ...(content.hero || {}) };
  if (!content.hero?.title && course?.title) hero.title = course.title;
  if (!content.hero?.breadcrumbLabel && (course?.shortTitle || course?.title)) {
    hero.breadcrumbLabel = course?.shortTitle || course?.title;
  }

  const overview = { ...DEFAULT_OVERVIEW, ...(content.overview || {}) };
  if (!content.overview?.description && course?.description) {
    overview.description = course.description;
  }

  const fallbackInfoCards = useMemo(() => {
    const duration = course?.duration || course?.teacherTraining?.duration;
    const level = course?.level;
    const certification = course?.teacherTraining?.certification || course?.home?.certification;
    const location = course?.location;

    return [
      {
        ...DEFAULT_INFO_CARDS[0],
        tag: duration || DEFAULT_INFO_CARDS[0].tag,
        detail: duration ? `📅 ${duration}` : DEFAULT_INFO_CARDS[0].detail,
      },
      {
        ...DEFAULT_INFO_CARDS[1],
        tag: level || DEFAULT_INFO_CARDS[1].tag,
        detail: level ? `📊 ${level}` : DEFAULT_INFO_CARDS[1].detail,
      },
      {
        ...DEFAULT_INFO_CARDS[2],
        tag: certification || DEFAULT_INFO_CARDS[2].tag,
        detail: certification ? `🏆 ${certification}` : DEFAULT_INFO_CARDS[2].detail,
      },
      {
        ...DEFAULT_INFO_CARDS[3],
        tag: location || DEFAULT_INFO_CARDS[3].tag,
        detail: location ? `📍 ${location}` : DEFAULT_INFO_CARDS[3].detail,
      },
    ];
  }, [course]);

  const infoCards = content.infoCards?.length ? content.infoCards : fallbackInfoCards;
  const whySection = { ...DEFAULT_WHY, ...(content.whyRishikesh || {}) };
  const curriculumSection = { ...DEFAULT_CURRICULUM, ...(content.curriculum || {}) };
  const scheduleSection = { ...DEFAULT_SCHEDULE, ...(content.dailySchedule || {}) };
  const featuresSection = { ...DEFAULT_FEATURES, ...(content.features || {}) };
  const includedSection = { ...DEFAULT_INCLUDED_SECTION, ...(content.included || {}) };
  const testimonialsSection = { ...DEFAULT_TESTIMONIALS, ...(content.testimonials || {}) };
  const faqSection = { ...DEFAULT_FAQS, ...(content.faqs || {}) };
  const ctaSection = { ...DEFAULT_CTA, ...(content.cta || {}) };

  const heroImage =
    toAssetUrl(hero.bannerImage || course?.banner || course?.card?.image) || heroVideo;
  const includedItems = includedSection.items?.length ? includedSection.items : DEFAULT_INCLUDED_SECTION.items;
  const notIncludedItems = includedSection.notIncluded || [];

  const courseTitle = hero.title || course?.title || DEFAULT_HERO.title;

  return (
    <>
      {/* Banner Image */}
      <div className={styles.bannerWrapper}>
        <img
          src={heroImage}
          alt={courseTitle}
          className={styles.bannerImage}
        />
      </div>

      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroContent}>
          <p className={styles.quoteText}>
            {hero.quoteText}
          </p>
          <h1 className={styles.mainTitle}>
            {hero.title}
          </h1>
          <div className={styles.breadcrumb}>
            <span className={styles.breadcrumbLink}>Home</span>
            <span className={styles.breadcrumbSeparator}>/</span>
            <span>{hero.breadcrumbLabel}</span>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className={styles.overviewSection}>
        <div className={styles.sectionHeader}>
          <p className={styles.sectionTag}>{overview.tag}</p>
          <h2 className={styles.sectionTitle}>
            {overview.title}
          </h2>
          <div className={styles.decorativeLine}></div>
          <p className={styles.sectionDescription}>
            {overview.description}
          </p>
        </div>

        {/* Info Grid */}
        <div className={styles.infoGrid}>
          {infoCards.map((card, index) => (
            <div className={styles.infoCard} key={index}>
              <div className={styles.infoContent}>
                <h3 className={styles.infoCardTitle}>{card.title}</h3>
                <div className={styles.infoTags}>
                  <span className={styles.infoTag}>{card.tag}</span>
                </div>
                <div className={styles.infoDetails}>
                  <p>{card.detail}</p>
                </div>
              </div>
              <div className={styles.infoImageSection}>
                <div className={styles.infoIcon}>{card.icon}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <Form100
        courseId={course?._id}
        courseTitle={courseTitle}
        batches={course?.batches || []}
        included={includedItems}
        notIncluded={notIncludedItems}
        toBringImage={includedSection.toBringImage}
        includedTitle={includedSection.includedTitle}
        notIncludedTitle={includedSection.notIncludedTitle}
        toBringTitle={includedSection.toBringTitle}
      />

      {/* Why Choose Our School Section */}
      <section className={styles.whyRishikeshSection}>
        <div className={styles.sectionHeader}>
          <p className={styles.sectionTag}>{whySection.tag}</p>
          <h2 className={styles.sectionTitle}>
            {whySection.title}
          </h2>
          <div className={styles.decorativeLine}></div>
          <p className={styles.sectionDescription}>
            {whySection.description}
          </p>
        </div>

        <div className={styles.contentGrid}>
          {(whySection.items || DEFAULT_WHY.items).map((item, index) => (
            <div key={index} className={styles.contentCard}>
              <div className={styles.contentInfo}>
                <h3 className={styles.contentCardTitle}>{item.title}</h3>
                <div className={styles.contentTags}>
                  <span className={styles.contentTag}>{item.icon}</span>
                </div>
                <div className={styles.contentDescription}>
                  <p>{item.description}</p>
                </div>
              </div>
              <div className={styles.contentIconSection}>
                <div className={styles.largeIcon}>{item.icon}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Retreat Highlights Section */}
      <section className={styles.curriculumSection}>
        <div className={styles.sectionHeader}>
          <p className={styles.sectionTag}>{curriculumSection.tag}</p>
          <h2 className={styles.sectionTitle}>
            {curriculumSection.title}
          </h2>
          <div className={styles.decorativeLine}></div>
        </div>

        <div className={styles.curriculumGrid}>
          {(curriculumSection.items || DEFAULT_CURRICULUM.items).map((item, index) => (
            <div key={index} className={styles.curriculumCard}>
              <div className={styles.curriculumInfo}>
                <h3 className={styles.curriculumTitle}>{item.category}</h3>
                <div className={styles.curriculumContent}>
                  <ul className={styles.curriculumList}>
                    {item.items.map((point, idx) => (
                      <li key={idx}>{point}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Retreat Schedule Section */}
      <section className={styles.scheduleSection}>
        <div className={styles.sectionHeader}>
          <p className={styles.sectionTag}>{scheduleSection.tag}</p>
          <h2 className={styles.sectionTitle}>
            {scheduleSection.title}
          </h2>
          <div className={styles.decorativeLine}></div>
        </div>

        <div className={styles.scheduleGrid}>
          {(scheduleSection.items || DEFAULT_SCHEDULE.items).map((item, index) => (
            <div key={index} className={styles.scheduleCard}>
              <div className={styles.scheduleInfo}>
                <h3 className={styles.scheduleTime}>{item.time}</h3>
                <div className={styles.scheduleActivity}>
                  <p>{item.activity}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Retreat Features Section */}
      <section className={styles.FeaturesSection}>
        <div className={styles.sectionHeader}>
          <p className={styles.sectionTag}>{featuresSection.tag}</p>
          <h2 className={styles.sectionTitle}>
            {featuresSection.title}
          </h2>
          <div className={styles.decorativeLine}></div>
        </div>

        <div className={styles.featuresGrid}>
          {(featuresSection.items || DEFAULT_FEATURES.items).map((feature, index) => (
            <div key={index} className={styles.featureCard}>
              <div className={styles.featureInfo}>
                <h3 className={styles.featureTitle}>{feature.title}</h3>
                <div className={styles.featureTags}>
                  <span className={styles.featureTag}>{feature.icon}</span>
                </div>
                <div className={styles.featureDescription}>
                  <p>{feature.desc}</p>
                </div>
              </div>
              <div className={styles.featureIconSection}>
                <div className={styles.featureIcon}>{feature.icon}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* What's Included Section */}
      <section className={styles.includedSection}>
        <div className={styles.sectionHeader}>
          <p className={styles.sectionTag}>{includedSection.tag}</p>
          <h2 className={styles.sectionTitle}>
            {includedSection.title}
          </h2>
          <div className={styles.decorativeLine}></div>
        </div>

        <div className={styles.includedGrid}>
          {includedItems.map((item, index) => (
            <div key={index} className={styles.includedCard}>
              <div className={styles.includedInfo}>
                <div className={styles.includedIcon}>✓</div>
                <p className={styles.includedText}>{item}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <Hours24 />

      {/* Testimonials Section */}
      <section className={styles.testimonialsSection}>
        <div className={styles.sectionHeader}>
          <p className={styles.sectionTag}>{testimonialsSection.tag}</p>
          <h2 className={styles.sectionTitle}>
            {testimonialsSection.title}
          </h2>
          <div className={styles.decorativeLine}></div>
        </div>

        <div className={styles.testimonialsGrid}>
          {(testimonialsSection.items || DEFAULT_TESTIMONIALS.items).map((testimonial, index) => (
            <div key={index} className={styles.testimonialCard}>
              <div className={styles.testimonialInfo}>
                <h3 className={styles.testimonialName}>{testimonial.name}</h3>
                <div className={styles.testimonialTags}>
                  <span className={styles.testimonialTag}>
                    {testimonial.country}
                  </span>
                </div>
                <div className={styles.testimonialText}>
                  <p>{testimonial.text}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <Refund />

      {/* FAQ Section */}
      <section className={styles.faqSection}>
        <div className={styles.sectionHeader}>
          <p className={styles.sectionTag}>{faqSection.tag}</p>
          <h2 className={styles.sectionTitle}>
            {faqSection.title}
          </h2>
          <div className={styles.decorativeLine}></div>
        </div>

        <div className={styles.faqGrid}>
          {(faqSection.items || DEFAULT_FAQS.items).map((faq, index) => (
            <div key={index} className={styles.faqCard}>
              <div className={styles.faqInfo}>
                <h3 className={styles.faqQuestion}>{faq.q}</h3>
                <div className={styles.faqAnswer}>
                  <p>{faq.a}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <OurCourses />
      <BooksSection />
      <HowToReach />

      {/* CTA Section */}
      <section className={styles.ctaSection}>
        <div className={styles.ctaContent}>
          <h2 className={styles.ctaTitle}>{ctaSection.title}</h2>
          <p className={styles.ctaSubtitle}>
            {ctaSection.subtitle}
          </p>
          <div className={styles.ctaButtons}>
            <button
              className={styles.ctaButton}
              onClick={() => navigate(ctaSection.primaryLink || '/BookingForm')}
            >
              {ctaSection.primaryLabel}
            </button>

            <button
              className={`${styles.ctaButton} ${styles.ctaButtonSecondary}`}
              onClick={() => navigate(ctaSection.secondaryLink || '/contact-us')}
            >
              {ctaSection.secondaryLabel}
            </button>
          </div>
        </div>
      </section>
    </>
  );
}
